import { useCallback, useState } from "react";

import { contarObras } from "@/modules/recursos";
import type { FornecedorDisponivel } from "@/modules/recursos";
import { JUSTIFICATIVA_GERAL_MAX, SIMULACAO_APLICAR_LABEL } from "@/modules/recursos/simulacao";

import { useFocoPrimeiroInvalido } from "../../hooks/use-foco-primeiro-invalido";
import { useRolarAteErro } from "../../hooks/use-rolar-ate-erro";
import {
  useIgnorarConflitoFornecedor,
  useRemanejarFornecedor,
} from "../../hooks/use-acoes-conflito-fornecedor";
import { formatIntervalo, obraTitulo } from "../resolver-conflito/resolver-conflito.utils";
import {
  FORMA_REQUIRED_MESSAGE,
  IGNORAR_SUBTITLE_PREFIX,
  JUSTIFICATIVA_MAX_LENGTH,
  JUSTIFICATIVA_REQUIRED_MESSAGE,
  OBRA_REQUIRED_MESSAGE,
  OPCAO_IGNORAR_TITLE,
  OPCAO_REMANEJAR_DESCRIPTION,
  OPCAO_REMANEJAR_TITLE,
  REMANEJAR_GUIDANCE,
  RESOLVER_TITLE,
  SELECIONAR_CONFLITO_TITLE,
  SUBSTITUTO_REQUIRED_MESSAGE,
} from "./resolver-conflito-fornecedor.constants";
import type { TratamentoTipo } from "./resolver-conflito-fornecedor.constants";
import {
  descreverSobreposicao,
  diasDoConflito,
  escopoEmConflito,
  intervaloCurto,
  plural,
  quantidadeDeObras,
} from "./resolver-conflito-fornecedor.utils";
import type {
  PropostaOpcao,
  ResolverConflitoFornecedorDialogProps,
  ResolverConflitoFornecedorDialogViewModel,
  ResolverConflitoFornecedorEtapa,
  ResolverConflitoFornecedorPrimario,
} from "./types-resolver-conflito-fornecedor";

/**
 * Modal "Resolver Conflito de Alocação" do fornecedor (Figma node 9051:51226). Com mais de um
 * conflito, o usuário escolhe qual tratar; depois decide entre Remanejar Fornecedor
 * (Escopos → Fornecedor → Revisão) e Ignorar Conflito (justificativa). Qualquer quantidade de
 * obras no conflito. O remanejamento troca o fornecedor da alocação; o escopo macro é só a
 * referência exibida da obra.
 */
export function useResolverConflitoFornecedorDialog({
  fornecedor,
  conflitos,
  escoposPorAlocacao,
  carga,
  periodo,
  today,
  canceladaVisivel,
  onResolved,
  onClose,
  simulacao,
}: ResolverConflitoFornecedorDialogProps): ResolverConflitoFornecedorDialogViewModel {
  const [etapa, setEtapa] = useState<ResolverConflitoFornecedorEtapa>(
    conflitos.length > 1 ? "conflito" : "proposta",
  );
  const [conflitoIndex, setConflitoIndex] = useState(0);
  const [tratamento, setTratamento] = useState<TratamentoTipo | null>(null);
  const [tratamentoError, setTratamentoError] = useState<string | null>(null);
  const [alocacaoId, setAlocacaoId] = useState<string | null>(null);
  const [escopoId, setEscopoId] = useState<string | null>(null);
  const [substituto, setSubstituto] = useState<FornecedorDisponivel | null>(null);
  const [search, setSearch] = useState("");
  const [obraError, setObraError] = useState<string | null>(null);
  const [substitutoError, setSubstitutoError] = useState<string | null>(null);
  const [justificativa, setJustificativa] = useState("");
  const [justificativaTouched, setJustificativaTouched] = useState(false);
  const [justificativaServerError, setJustificativaServerError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // Os hooks reais ficam montados (Rules of Hooks); com `simulacao` o confirmar
  // retorna antes de remanejar/ignorar na API.
  const { corpoRef, focarPrimeiroInvalido } = useFocoPrimeiroInvalido();
  const erroRef = useRolarAteErro(serverError);
  const { remanejar, isSubmitting } = useRemanejarFornecedor();
  const { ignorar } = useIgnorarConflitoFornecedor();

  const conflito = conflitos[conflitoIndex];
  const temConflito = Boolean(conflito && conflito.alocacaoIds.length >= 2);
  const alocacao = conflito?.alocacoes.find((item) => item.id === alocacaoId) ?? null;
  const escopos = (alocacao ? escoposPorAlocacao[alocacao.id] : undefined) ?? [];
  const escopo = escopos.find((item) => item.id === escopoId) ?? null;
  const obras = conflito ? contarObras(conflito) : 0;

  const justificativaRequired = justificativa.trim() === "" ? JUSTIFICATIVA_REQUIRED_MESSAGE : null;

  // Intenções que os passos usam como dependência: identidade estável.
  const escolherObra = useCallback(
    (id: string) => {
      setAlocacaoId(id);
      setObraError(null);
      // Pré-seleciona o escopo que causa a sobreposição (ou o primeiro); a escolha é só visual.
      const lista = escoposPorAlocacao[id] ?? [];
      const padrao =
        (conflito ? lista.find((item) => escopoEmConflito(item, conflito)) : undefined) ?? lista[0];
      setEscopoId(padrao?.id ?? null);
    },
    [escoposPorAlocacao, conflito],
  );
  const escolherEscopo = useCallback((id: string) => setEscopoId(id), []);
  const escolherSubstituto = useCallback((item: FornecedorDisponivel) => {
    setSubstituto(item);
    setSubstitutoError(null);
  }, []);
  const buscar = useCallback((texto: string) => setSearch(texto), []);

  function fechar() {
    if (!isSubmitting) onClose();
  }

  function escolherTratamento(valor: TratamentoTipo) {
    setTratamento(valor);
    setTratamentoError(null);
  }

  function avancarProposta() {
    if (!tratamento) {
      setTratamentoError(FORMA_REQUIRED_MESSAGE);
      focarPrimeiroInvalido();
      return;
    }
    setEtapa(tratamento === "remanejar" ? "escopos" : "ignorar");
  }

  function avancarEscopos() {
    if (!alocacao) {
      setObraError(OBRA_REQUIRED_MESSAGE);
      focarPrimeiroInvalido();
      return;
    }
    setObraError(null);
    setEtapa("substituto");
  }

  function avancarSubstituto() {
    if (!substituto) {
      setSubstitutoError(SUBSTITUTO_REQUIRED_MESSAGE);
      focarPrimeiroInvalido();
      return;
    }
    setServerError(null);
    setEtapa("revisao");
  }

  function voltarPara(destino: ResolverConflitoFornecedorEtapa) {
    setServerError(null);
    setEtapa(destino);
  }

  async function confirmarRemanejamento() {
    if (isSubmitting || !conflito || !alocacao || !substituto) return;
    setJustificativaTouched(true);
    if (justificativaRequired) {
      focarPrimeiroInvalido();
      return;
    }
    // Com `simulacao`, nunca grava.
    if (simulacao) {
      setServerError(null);
      const resultado = await simulacao.remanejar({
        conflito,
        alocacao,
        substituto,
        escopo: escopo?.escopo,
        justificativa: justificativa.trim(),
      });
      if (resultado.ok) onResolved({ tipo: "simulado" });
      else if (resultado.mensagem) setServerError(resultado.mensagem);
      return;
    }

    setJustificativaServerError(null);
    setServerError(null);
    const result = await remanejar({
      alocacaoId: alocacao.id,
      fornecedorSubstitutoId: substituto.id,
      justificativa: justificativa.trim(),
    });
    if (result.ok) {
      onResolved({ tipo: "remanejado" });
      return;
    }
    if (result.kind === "invalid") {
      setJustificativaServerError(result.message);
      return;
    }
    setServerError(result.message);
    // O substituto ficou indisponível: volta à seleção para escolher outro.
    if (result.kind === "indisponivel") {
      setSubstituto(null);
      setEtapa("substituto");
    }
  }

  function primarioDaEtapa(): ResolverConflitoFornecedorPrimario | null {
    if (!temConflito) return null;
    const botao = (rotulo: string, testId: string, acionar: () => void) => ({
      rotulo,
      testId,
      acionar,
      carregando: false,
      desabilitado: false,
    });
    switch (etapa) {
      case "conflito":
        return botao("Avançar", "resolver-conflito-avancar", () => setEtapa("proposta"));
      case "proposta":
        return botao(
          tratamento === "ignorar" ? (simulacao ? SIMULACAO_APLICAR_LABEL : "Salvar") : "Avançar",
          "resolver-conflito-avancar",
          avancarProposta,
        );
      case "escopos":
        return botao("Próximo Passo", "resolver-conflito-proximo", avancarEscopos);
      case "substituto":
        return botao("Próximo Passo", "resolver-conflito-proximo", avancarSubstituto);
      case "revisao":
        return {
          ...botao(
            simulacao ? SIMULACAO_APLICAR_LABEL : "Confirmar Mudança",
            "resolver-conflito-confirmar",
            () => void confirmarRemanejamento(),
          ),
          carregando: isSubmitting,
          desabilitado: Boolean(simulacao && justificativaRequired),
        };
      default:
        return null;
    }
  }

  let voltar: (() => void) | null = null;
  if (temConflito && etapa === "escopos") voltar = () => voltarPara("proposta");
  if (temConflito && etapa === "substituto") voltar = () => voltarPara("escopos");
  if (temConflito && etapa === "revisao") voltar = () => voltarPara("substituto");

  const noRemanejar = etapa === "escopos" || etapa === "substituto" || etapa === "revisao";
  const continuaEm = conflito
    ? conflito.alocacoes
        .filter((item) => item.obra.id !== alocacao?.obra.id)
        .map((item) => item.obra)
        .filter((obra, index, lista) => lista.findIndex((outra) => outra.id === obra.id) === index)
    : [];

  const opcoesDaProposta: Omit<PropostaOpcao, "testId" | "marcada">[] = [
    {
      value: "remanejar",
      label: OPCAO_REMANEJAR_TITLE,
      description: OPCAO_REMANEJAR_DESCRIPTION,
      icone: "pi pi-times-circle",
      recomendada: true,
    },
    {
      value: "ignorar",
      label: OPCAO_IGNORAR_TITLE,
      description: `Manter o fornecedor nas ${quantidadeDeObras(obras)} obras. Informe o motivo e a sobreposição passa a aparecer em verde no cronograma, como conflito tratado.`,
      icone: "pi pi-eye-slash",
      recomendada: false,
    },
  ];

  return {
    etapa,
    titulo: etapa === "conflito" ? SELECIONAR_CONFLITO_TITLE : RESOLVER_TITLE,
    orientacao: temConflito && noRemanejar ? REMANEJAR_GUIDANCE : null,
    simulando: Boolean(simulacao),
    isSubmitting,
    semConflito: !temConflito,
    serverError,
    resumo:
      conflito && temConflito
        ? etapa === "conflito"
          ? {
              nome: fornecedor.nome,
              alerta: `Este fornecedor tem ${conflitos.length} conflitos no cronograma`,
              obras: [],
              sobreposicao: null,
            }
          : {
              nome: fornecedor.nome,
              alerta: `Este fornecedor está alocado em ${obras} obras com datas sobrepostas`,
              obras: conflito.alocacoes.map((item) => ({
                key: item.id,
                titulo: obraTitulo(item.obra),
                intervalo: formatIntervalo(item),
              })),
              sobreposicao: `Sobreposição de ${plural(diasDoConflito(conflito), "dia", "dias")}: ${intervaloCurto(conflito)}`,
            }
        : null,
    escolha: {
      opcoes: conflitos.map((item, index) => ({
        key: item.alocacaoIds.join("|"),
        titulo: item.alocacoes.map((alocacaoItem) => obraTitulo(alocacaoItem.obra)).join(" × "),
        descricao: `Alocado em ${contarObras(item)} obras. Sobreposição: ${descreverSobreposicao(item)}`,
        marcada: index === conflitoIndex,
      })),
      escolher: (index) => {
        setConflitoIndex(index);
        setAlocacaoId(null);
        setEscopoId(null);
        setSubstituto(null);
      },
    },
    proposta: {
      opcoes: opcoesDaProposta.map((opcao) => ({
        ...opcao,
        testId: `resolver-conflito-opcao-${opcao.value}`,
        marcada: tratamento === opcao.value,
      })),
      erro: tratamentoError,
      escolher: escolherTratamento,
    },
    escopos:
      etapa === "escopos" && conflito && temConflito
        ? {
            fornecedor,
            conflito,
            escoposPorAlocacao,
            alocacaoId,
            escopoId,
            erroObra: obraError,
            escolherObra,
            escolherEscopo,
          }
        : null,
    substituto:
      etapa === "substituto" && temConflito && alocacao
        ? {
            fornecedor,
            alocacao,
            escopo,
            carga,
            substituto,
            search,
            erroSubstituto: substitutoError,
            escolherSubstituto,
            buscar,
          }
        : null,
    revisao:
      etapa === "revisao" && temConflito && alocacao && substituto
        ? {
            fornecedor,
            alocacao,
            escopo: escopo?.escopo ?? null,
            continuaEm,
            substituto,
            carga,
            periodo,
            today,
            canceladaVisivel,
            disabled: isSubmitting,
            justificativa: {
              value: justificativa,
              error:
                justificativaServerError ?? (justificativaTouched ? justificativaRequired : null),
              maxLength: simulacao ? JUSTIFICATIVA_GERAL_MAX : JUSTIFICATIVA_MAX_LENGTH,
              alterar: (valor) => {
                setJustificativa(valor);
                setJustificativaServerError(null);
              },
            },
          }
        : null,
    corpoRef,
    erroRef,
    ignorar:
      etapa === "ignorar" && conflito && temConflito
        ? {
            descricao: `${IGNORAR_SUBTITLE_PREFIX} as ${quantidadeDeObras(obras)} obras no mesmo período.`,
            mensagemObrigatoria: JUSTIFICATIVA_REQUIRED_MESSAGE,
            onCancel: () => setEtapa("proposta"),
            cancelLabel: "Voltar",
            ocultarDicaObrigatorio: true,
            confirmLabel: simulacao ? SIMULACAO_APLICAR_LABEL : undefined,
            maxLength: simulacao ? JUSTIFICATIVA_GERAL_MAX : undefined,
            onConfirm: async (texto) => {
              // Modo Simulação: a justificativa da ação segue no cenário (comentários da 2224 e 2221).
              if (simulacao) {
                const resultado = await simulacao.ignorar({ conflito, justificativa: texto });
                if (resultado.ok) onResolved({ tipo: "simulado" });
                else if (resultado.mensagem)
                  return { kind: "error" as const, message: resultado.mensagem };
                return;
              }
              const result = await ignorar({
                fornecedorId: fornecedor.id,
                conflitoInicio: conflito.inicio,
                conflitoFim: conflito.fim,
                alocacaoIds: conflito.alocacaoIds,
                justificativa: texto,
              });
              if (result.ok) {
                onResolved({ tipo: "ignorado", alocacaoIds: conflito.alocacaoIds });
                return;
              }
              return result;
            },
          }
        : null,
    rodape: { voltar, primario: primarioDaEtapa() },
    fechar,
  };
}
